import {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Translate} from 'react-redux-i18n';
import removeAccents from 'remove-accents';
import {parse} from 'query-string';
import {withRouter} from 'react-router-dom';

import {Icon, SidePanel} from 'front-onceforall-core';
import {DOCUMENT_STATES_VALID} from 'front-onceforall-core/dist/utils/documents';
import {DOSSIER_STATE} from 'front-onceforall-core/dist/utils/dossiers';
import {isEmpty as isArrayEmpty} from 'front-onceforall-core/dist/utils/arrays';

import * as snackbarsActions from '../../actions/snackbars';
import documentService from '../../services/document/DocumentService';
import authenticationService from '../../services/authentication/AuthenticationService';
import organisationService from '../../services/organisation/OrganisationService';
import subscriptionService from '../../services/subscription/SubscriptionService';
import signatureService from '../../services/signature/SignatureService';
import {
  getDistinctCountries, getDocumentsByCountry, getFakeDocumentsByCountry, hasStandardFiles, hasValidOrNotApplicableFiles, getFirstDossierCountry, setFirstDossierCountry
} from '../../utils/document';
import {openOrSaveFile} from '../../utils/http';
import {universignTransactionStatus, USER_REQUEST_TYPES} from '../../utils/constant';
import {captureError} from '../../utils/log';
import DossierActions, {
  createActionAddDocument,
  createActionDossier,
  createDownloadDossier,
  createDownloadDossierOverview,
  createPrintDossier
} from '../../components/dossier/dossierActions/DossierActions';
import {handleDownloadDossierOverview} from '../../components/dossier/utils';
import {DossierOverviewAndActions} from '../../components/dossier/dossierOverviewAndActions/DossierOverviewAndActions';
import AddDocumentForm from '../../components/dossier/addDocument/AddDocumentForm';
import DossierStateResume from '../../components/dossier/DossierStateResume';
import LinkButtonView from '../../components/buttons/link';
import DossierCountrySelector from '../../components/dossierCountrySelector';
import OverlayMessage from '../../components/overlayMessage';
import DocumentListSearch from '../../components/DocumentListSearch';
import DiscoverVitrineBox from '../../components/DiscoverVitrineBox';
import ToggleVitrineBoxButton from '../../components/DiscoverVitrineBox/ToggleVitrineBoxButton';
import DiscoverBdrBox from '../../components/DiscoverBdrBox';
import ToggleBdrBoxButton from '../../components/DiscoverBdrBox/ToggleBdrBoxButton';
import {recordGAEvent} from '../../utils/analytics';

export class MyDocumentsView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dossierState: null,
      submitted: false,
      documents: [],
      filteredDocuments: [],
      archivedDocuments: [],
      depositor: false,
      plan: {},
      loading: true,
      loadingState: true,
      loadingArchivedDocuments: true,
      loadingDepositor: true,
      error: false,
      transactionId: parse(props.location.search).transactionId || null,
      searchDocumentValue: '',
      addADocumentSidePanelIsOpen: false,
      dossierCountry: '',
      countries: []
    };

    this.fetchDossierState = this.fetchDossierState.bind(this);
    this.getDocuments = this.getDocuments.bind(this);
    this.handlePrintDossier = this.handlePrintDossier.bind(this);
    this.handleDownloadDossier = this.handleDownloadDossier.bind(this);
    this.handleDownloadArchivesDossier = this.handleDownloadArchivesDossier.bind(this);
    this.fetchOrganisationDocuments = this.fetchOrganisationDocuments.bind(this);
    this.fetchPromiseOrganisationDocuments = this.fetchPromiseOrganisationDocuments.bind(this);
    this.fetchOrganisationDetails = this.fetchOrganisationDetails.bind(this);
    this.fetchPromiseOrganisationDetails = this.fetchPromiseOrganisationDetails.bind(this);
    this.callbackSignedDocument = this.callbackSignedDocument.bind(this);
    this.handleRefreshDocuments = this.handleRefreshDocuments.bind(this);
    this.handleAddDocumentSidePanel = this.handleAddDocumentSidePanel.bind(this);
    this.handleShowArchives = this.handleShowArchives.bind(this);
    this.handleDossierCountryChange = this.handleDossierCountryChange.bind(this);
    this.buildSelectCountries = this.buildSelectCountries.bind(this);
    this.handleRemind = this.handleRemind.bind(this);
    this.getSubscriptionPlan = this.getSubscriptionPlan.bind(this);

    const roles = authenticationService.roles();
    this.isManager = roles.isManager();
    this.isExplorer = roles.isExplorer();
  }

  componentDidMount() {
    this.callbackSignedDocument(this.state.transactionId);

    const {forceDossierCountry} = parse(this.props.location.search);
    if (forceDossierCountry) {
      setFirstDossierCountry(forceDossierCountry);
    }

    Promise.all([
      this.fetchPromiseOrganisationDocuments(),
      this.fetchPromiseOrganisationDetails()
    ]).then(() => {
      if (!this.state.depositor && this.state.documents.length === 0) {
        const fakeDocuments = getFakeDocumentsByCountry(this.props.organisation.country);
        this.setState({
          documents: fakeDocuments,
          archivedDocuments: [],
          loadingState: false,
          dossierState: {
            dossierState: DOSSIER_STATE.INVALID,
            invalidDocuments: [],
            almostExpiredDocuments: []
          },
          countries: [this.props.organisation.country],
          dossierCountry: this.props.organisation.country
        });
      }
    });
    this.fetchArchivedDocuments();
  }

  componentDidUpdate(prevProps) {
    // This means we are coming from another URL
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.fetchOrganisationDocuments();
    }
  }

  callbackSignedDocument(transactionId) {
    if (!transactionId) {
      return;
    }

    signatureService.sendSignedDocument(transactionId)
      .then(result => {
        if (result !== null) {
          if (result.status === universignTransactionStatus.COMPLETED) {
            this.props.actions.addSuccess(<Translate value="signature.successMessage"/>);
          } else if (result.status === universignTransactionStatus.CANCELED) {
            this.props.actions.addSuccess(<Translate value="signature.canceledMessage"/>);
          } else if (result.status === universignTransactionStatus.FAILED) {
            this.props.actions.addError(<Translate value="error.generic"/>);
          }

          this.handleRefreshDocuments();
        }
      })
      .catch(error => {
        captureError('callbackSignedDocument', error);
        this.props.actions.addError(<Translate value="error.generic"/>);
      });
  }

  fetchDossierState() {
    this.setState({dossierState: null, loadingState: true});

    if (!this.state.dossierCountry) {
      this.setState({loadingState: false});
      return;
    }

    const {organisationId} = this.props.organisation;
    documentService.getDossierStateDetails(organisationId, this.state.dossierCountry)
      .then(response => this.setState({
        dossierState: response,
        loadingState: false
      }))
      .catch(() => this.setState({
        dossierState: null,
        loadingState: false
      }));
  }

  onDownloadDossierOverview = () => {
    recordGAEvent('SUPPLIER_QUITUS', 'Download Quitus');
    handleDownloadDossierOverview(this.props.organisation.organisationId, this.props.currentLocale, this.state.dossierCountry);
  };

  handlePrintDossier() {
    if (!hasStandardFiles(this.state.documents) || this.state.submitted) {
      return;
    }

    this.setState({submitted: true});
    recordGAEvent('SUPPLIER_DOSSIER', 'Print folder');
    authenticationService.authRedirect(
      documentService.getDossierPdfUrl(this.props.organisation.organisationId, this.state.dossierCountry)
    )
      .then(authUrl => {
        openOrSaveFile(authUrl);
        this.setState({submitted: false});
      });
  }

  handleDownloadDossier() {
    if (!hasStandardFiles(this.state.documents) || this.state.submitted) {
      return;
    }

    this.setState({submitted: true});
    recordGAEvent('SUPPLIER_DOSSIER', 'Download folder');
    authenticationService.authRedirect(
      documentService.getDossierZipUrl(this.props.organisation.organisationId, this.state.dossierCountry)
    )
      .then(authUrl => {
        openOrSaveFile(authUrl);
        this.setState({submitted: false});
      });
  }

  handleDownloadArchivesDossier() {
    if (!this.state.archivedDocuments.length || this.state.submitted) {
      return;
    }

    this.setState({submitted: true});
    recordGAEvent('SUPPLIER_ARCHIVES', 'Download archives');
    authenticationService.authRedirect(
      documentService.getDossierArchivesZipUrl(this.props.organisation.organisationId)
    )
      .then(authUrl => {
        openOrSaveFile(authUrl);
        this.setState({submitted: false});
      });
  }

  handleAddDocumentSidePanel(addADocumentSidePanelIsOpen) {
    return () => {
      this.setState({addADocumentSidePanelIsOpen});
    };
  }

  handleAddADocument(success) {
    return () => {
      this.triggerCloseAddDocument();
      if (success) {
        this.fetchOrganisationDocuments();
      }
    };
  }

  fetchPromiseOrganisationDocuments() {
    return new Promise(resolve => {
      this.fetchOrganisationDocuments(resolve);
    });
  }

  fetchOrganisationDocuments(callbackSuccess = () => {}) {
    this.setState({
      loading: true,
      error: false
    });

    documentService.search(this.props.organisation.organisationId)
      .then(response => {
        const documents = response.map(document => ({...document, searchValue: removeAccents(document.type.name.toLowerCase())}));

        const countries = getDistinctCountries(response);
        const dossierCountry = getFirstDossierCountry(countries, this.state.dossierCountry, this.props.organisation.country);

        this.setState({
          loading: false,
          documents,
          countries,
          dossierCountry
        }, () => {
          callbackSuccess();
          this.fetchDossierState();
        });
      })
      .catch(() => {
        this.setState({
          loading: false,
          error: true,
          documents: [],
          countries: []
        });
      });
  }

  fetchArchivedDocuments() {
    this.setState({loadingArchivedDocuments: true});
    documentService.getArchivedDocuments(this.props.organisation.organisationId)
      .then(response => {
        // In FO, we only want to see valid archived documents
        const archivedDocuments = response
          .filter(document => DOCUMENT_STATES_VALID.includes(document.state))
          .map(document => ({...document, searchValue: removeAccents(document.type.name.toLowerCase())}));
        this.setState({
          archivedDocuments,
          loadingArchivedDocuments: false
        });
      })
      .catch(() => {
        this.setState({
          error: true,
          archivedDocuments: [],
          loadingArchivedDocuments: false
        });
      });
  }

  handleRefreshDocuments() {
    this.fetchOrganisationDocuments();
  }

  fetchPromiseOrganisationDetails() {
    return new Promise(resolve => {
      this.fetchOrganisationDetails(resolve);
    });
  }

  async fetchOrganisationDetails(callbackSuccess = () => {}) {
    try {
      const currentOrganisation = await organisationService.get(this.props.organisation.organisationId);
      let plan = {};
      if (!currentOrganisation.depositor) {
        // If not a depositor, we need to know about the plan status
        plan = await this.getSubscriptionPlan(this.props.organisation.organisationId);
      }

      this.setState({
        plan,
        depositor: currentOrganisation.depositor,
        loadingDepositor: false
      }, () => {
        callbackSuccess();
      });
    } catch (error) {
      captureError('Error while fetching organisation details', error);
    }
  }

  async getSubscriptionPlan(organisationId) {
    try {
      const [currentSubscription, ofaPlan] = await Promise.all([
        subscriptionService.findSubscriptionByOrganisation(organisationId),
        subscriptionService.getOrganisationPlan(organisationId)
      ]);

      if (!currentSubscription || !ofaPlan) {
        // Can't determine plan
        return {};
      }

      return {
        depositorServiceAuthorized: currentSubscription.depositorServiceAuthorized,
        upgradable: ofaPlan.upgradable
      };
    } catch (error) {
      captureError('Error while loading plan details', error);
      // No need to fail page for that
      return {};
    }
  }

  getDocuments() {
    return getDocumentsByCountry(this.state.documents, this.state.dossierCountry);
  }

  handleShowArchives() {
    this.props.history.push('/documents/archives');
  }

  handleDossierCountryChange(event) {
    const dossierCountry = event.target.value;
    this.setState({
      dossierCountry
    }, () => {
      this.fetchDossierState();
    });
    setFirstDossierCountry(dossierCountry);
  }

  buildSelectCountries() {
    return (
      <DossierCountrySelector
        onChange={this.handleDossierCountryChange}
        value={this.state.dossierCountry}
        countries={this.state.countries}
        disabled={!this.state.depositor}
        data-id="dossier-country-filter"
      />
    );
  }

  handleRemind() {
    this.props.history.push(`/documents/${this.props.organisation.organisationId}/remind?country=${this.props.organisation.country}`);
  }

  renderOverlayMessage() {
    if (this.state.loadingDepositor) {
      return (
        <div className="loading-overlay"/>
      );
    }

    if (this.isExplorer && this.state.depositor) {
      return (
        <OverlayMessage
          type="role"
          message={<Translate value="message.roleNotAllowed"/>}
        >
          <LinkButtonView
            link={`${this.props.location.pathname}?contact=${USER_REQUEST_TYPES.MY_ROLE}`}
            buttonLabel={<Translate value="contact.title"/>}
          />
        </OverlayMessage>
      );
    }

    if (!this.state.depositor) {
      // Collector only with upgradable plan can upgrade their offer in autonomy
      if (!this.state.plan.depositorServiceAuthorized && this.state.plan.upgradable) {
        return (
          <OverlayMessage
            type="document"
            message={<Translate value="document.planUpgradable"/>}
          >
            <LinkButtonView
              link={'/register-organisation-plan'}
              buttonLabel={<Translate value="plan.actions.upgrade"/>}
            />
          </OverlayMessage>
        );
      }

      // Otherwise client has to contact the customer service
      return (
        <OverlayMessage
          type="document"
          message={<Translate value="document.notDepositor"/>}
        >
          <LinkButtonView
            link={`${this.props.location.pathname}?contact=${USER_REQUEST_TYPES.MY_OFFER}`}
            buttonLabel={<Translate value="plan.actions.contactUs"/>}
          />
        </OverlayMessage>
      );
    }

    if (isArrayEmpty(this.state.documents) && !this.state.loadingDepositor && !this.state.loading) {
      return (
        <OverlayMessage
          type="document"
          message={<Translate value="document.noDocumentMessage"/>}
        >
          <LinkButtonView
            link="/clients?add"
            buttonLabel={<Translate value="client.add.button"/>}
          />
        </OverlayMessage>
      );
    }

    return null;
  }

  renderMenuActions() {
    if (!this.state.documents.length && !this.state.loadingState) {
      return null;
    }

    const menuActions = [
      createDownloadDossierOverview(this.onDownloadDossierOverview),
      createPrintDossier(this.handlePrintDossier, 'dossier.print.standard', !hasValidOrNotApplicableFiles(this.state.documents)),
      createDownloadDossier(this.handleDownloadDossier, 'dossier.download.standard', !hasValidOrNotApplicableFiles(this.state.documents)),
      createActionDossier(this.handleDownloadArchivesDossier, 'dossier.download.standardArchives', 'download', isArrayEmpty(this.state.archivedDocuments))
    ];

    if (this.isManager) {
      menuActions.push(createActionAddDocument(this.handleAddDocumentSidePanel(true)));
    }

    if (!this.isExplorer && this.state.depositor) {
      menuActions.push(createActionDossier(this.handleRemind, 'document.remind.command.title', 'notification'));
    }

    return (<>
      {this.state.loadingState && <Icon icon="loader"/>}
      {!this.state.loadingState &&
      <DossierOverviewAndActions
        downloadDisabled={!this.state.depositor}
        downloadDisabledTooltipKey="organisation.depositorService.notActivated"
      >
        <DossierActions
          menuActions={menuActions}
          big
        />
      </DossierOverviewAndActions>}
    </>);
  }

  renderHeader() {
    return (
      <div className="row mt-4">
        <h1 className="col-md-6 m-0">
          <Translate value="document.myDocuments"/>
          <span
            data-id="number-of-documents"
            className="separator information"
          >{this.state.filteredDocuments.length}</span>
        </h1>
        <div className="col-md-6 flex-container flex-end">
          <div className="flex-container">
            <ToggleBdrBoxButton/>
            <ToggleVitrineBoxButton/>
            {this.state.countries.length > 1 && this.buildSelectCountries()}
            <button
              type="button"
              data-id="consult-my-archived-documents"
              className="inline-container secondary large no-wrap ml-4"
              onClick={this.handleShowArchives}
              disabled={this.state.archivedDocuments.length < 1}
            >
              <Translate value="document.consultMyArchivedDocuments"/>
              <span
                data-id="number-of-archived-documents"
                className={`badge ${this.state.archivedDocuments?.length > 99 ? 'more-characters' : ''}`}
              >
                <span className={this.state.archivedDocuments.length < 1 ? 'information' : ''}>
                  {this.state.loadingArchivedDocuments ?
                    <Icon icon="loader"/> :
                    this.state.archivedDocuments.length
                  }
                </span>
              </span>
            </button>
            <div>
              {this.renderMenuActions()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <>
        {this.state.loading && <div className="loading-overlay"/>}
        {this.renderHeader()}
        {this.renderOverlayMessage()}
        <DiscoverBdrBox/>
        <DiscoverVitrineBox/>
        {this.state.documentsError ? <span><Translate value="error.generic"/></span> :
          <>
            <DossierStateResume
              loading={this.state.loadingState}
              dossierState={this.state.dossierState}
              dossierCountry={this.state.dossierCountry}
            />
            <DocumentListSearch
              documents={this.state.documents}
              dossierCountry={this.state.dossierCountry}
              loading={this.state.loading}
              organisationId={this.props.organisation?.organisationId}
              onRefreshDocuments={this.handleRefreshDocuments}
              onFilterDocuments={filteredDocuments => {
                this.setState({filteredDocuments});
              }}
              depositor={this.state.depositor}
            />
          </>
        }
        {this.state.addADocumentSidePanelIsOpen &&
        <SidePanel
          onClose={this.handleAddDocumentSidePanel(false)}
          setCloseTrigger={closeFunction => {
            this.triggerCloseAddDocument = closeFunction;
          }}
          data-id="side-panel-add-a-document"
        >
          <AddDocumentForm
            onSuccess={this.handleAddADocument(true)}
            onCancel={this.handleAddADocument(false)}
            organisation={this.props.organisation}
            dossierCountry={this.state.dossierCountry}
          />
        </SidePanel>}
      </>
    );
  }
}

MyDocumentsView.propTypes = {
  actions: PropTypes.object.isRequired,
  organisation: PropTypes.object.isRequired,
  currentLocale: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string
  }).isRequired,
  history: PropTypes.object.isRequired
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation,
    currentLocale: state.i18n.locale
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({...snackbarsActions}, dispatch)
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MyDocumentsView));
